import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { X } from 'lucide-react';
import { memo, useState } from 'react';

const DialogStyled = styled(Dialog)(() => {
    return {
        '& .MuiDialog-paper': {
            borderRadius: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
        },
        '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            '& .input-section': {
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                '& .MuiTypography-root': {
                    color: '#6B7985',
                    fontSize: '15px',
                    lineHeight: '20px',
                },
                '& .MuiButton-root': {
                    padding: '8px 16px',
                    borderRadius: '8px',
                    borderColor: '#E4E6EB',
                    backgroundColor: '#FFFFFF',
                    color: '#3A4B59',
                    minHeight: '40px',
                    maxHeight: '40px',
                },
                '& .selected': {
                    backgroundColor: '#6A76FB',
                    color: 'white',
                },
            },
        },
    };
});

const RenameProspectCollectionModalMemoized = ({
    onClose,
    onSubmit,
    invalidNames,
    oldName,
    open,
}: {
    onClose: () => void;
    onSubmit: (name: string) => void;
    invalidNames: string[];
    oldName: string;
    open: boolean;
}) => {
    const [name, setName] = useState<string>('');
    const [hasError, setHasError] = useState<boolean>(false);

    const handleSubmit = () => {
        onSubmit(name);
        onClose();
    };

    const handleNameChange = (value: string) => {
        setName(value);
        if (invalidNames.includes(value)) {
            setHasError(true);
            return;
        }
        setHasError(value.length === 0);
    };

    return (
        <DialogStyled fullWidth open={open} onClose={onClose}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                }}
            >
                <DialogTitle
                    sx={{
                        padding: '24px 24px 0',
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                color: '#23323F',
                                fontSize: '19px',
                                fontWeight: 500,
                                lineHeight: '28px',
                            }}
                        >
                            Rename Prospect Collection "{oldName}"
                        </Typography>
                    </Box>
                    <IconButton sx={{ position: 'absolute', top: '24px', right: '16px' }} onClick={onClose}>
                        <X size={20} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box className="input-section">
                        <Typography>New Collection Name</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '16px',
                            }}
                        >
                            <TextField
                                variant="outlined"
                                fullWidth
                                error={hasError}
                                value={name}
                                onChange={(e) => handleNameChange(e.target.value)}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: '0px 24px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '16px',
                        }}
                    >
                        <Button
                            variant="outlined"
                            disableElevation
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                borderColor: '#E4E6EB',
                                backgroundColor: '#FFFFFF',
                                color: '#3A4B59',
                                minHeight: '40px',
                                maxHeight: '40px',
                            }}
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={hasError}
                            disableElevation
                            sx={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                backgroundColor: '#6A76FB',
                                minHeight: '40px',
                                maxHeight: '40px',
                                minWidth: '123px',
                                transition: 'background-color 0.3s, transform 0.3s',
                                ':hover': {
                                    backgroundColor: '#838dfc',
                                },
                            }}
                            onClick={handleSubmit}
                        >
                            <Typography
                                component={'span'}
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    lineHeight: '21px',
                                    color: 'white',
                                    transition: 'color 0.3s',
                                }}
                            >
                                Rename
                            </Typography>
                        </Button>
                    </Box>
                </DialogActions>
            </Box>
        </DialogStyled>
    );
};

export const RenameProspectCollectionModal = memo(RenameProspectCollectionModalMemoized);
